/**
 * Endpoints.
 *
 * Una función por ruta del backend. Nada de lógica acá: solo la firma tipada
 * y la URL. Si el backend cambia una ruta, este es el único archivo que se
 * toca en todo el frontend.
 *
 * Las rutas que todavía no existen del lado del servidor (turnos, pagos,
 * sync por lotes) están marcadas abajo con `PENDIENTE` para que quede
 * explícito qué falta y no se descubra en producción.
 */

import { pedir } from './cliente';
import type {
  Arqueo,
  Categoria,
  Producto,
  Mesa,
  MetodoPago,
  OrdenPendiente,
  Orden,
  OrdenDetalle,
  OrdenItem,
  RespuestaCatalogo,
  PuestoNuevo,
  RespuestaLogin,
  RespuestaPago,
  RespuestaTurnoActual,
  TipoOrden,
  Turno,
} from '../tipos/contrato';

/* ─────────────────────────────── Catálogo ─────────────────────────────── */

/**
 * Administrar el catálogo va DIRECTO al servidor, sin pasar por el outbox.
 *
 * No es una operación de servicio sino de configuración: se hace de mañana con
 * el local vacío, no con seis mesas esperando. Encolarla ofrecería poco y
 * traería un problema real — un producto creado sin conexión no tiene id del
 * servidor, así que los pedidos que lo usaran quedarían apuntando a un
 * producto que todavía no existe.
 */
export const crearCategoria = (datos: { nombre: string; color?: string; orden?: number }) =>
  pedir<Categoria>('/api/categorias', { metodo: 'POST', cuerpo: datos });

export const actualizarCategoria = (
  id: string,
  datos: { nombre?: string; color?: string; orden?: number },
) => pedir<Categoria>(`/api/categorias/${id}`, { metodo: 'PATCH', cuerpo: datos });

export interface DatosProducto {
  nombre: string;
  precio: number;
  categoriaId?: string | null;
  descripcion?: string;
  requiereCocina?: boolean;
  tiempoPreparacion?: number | null;
  disponible?: boolean;
  orden?: number;
}

export const crearProducto = (datos: DatosProducto) =>
  pedir<Producto>('/api/productos', { metodo: 'POST', cuerpo: datos });

export const actualizarProducto = (id: string, datos: Partial<DatosProducto>) =>
  pedir<Producto>(`/api/productos/${id}`, { metodo: 'PATCH', cuerpo: datos });

export const eliminarProducto = (id: string) =>
  pedir<void>(`/api/productos/${id}`, { metodo: 'DELETE' });

/** Agotar o reactivar. Esta sí la usa cualquier rol durante el servicio. */
export const cambiarDisponibilidad = (id: string, disponible: boolean) =>
  pedir<Producto>(`/api/productos/${id}/disponibilidad`, {
    metodo: 'POST',
    cuerpo: { disponible },
  });

/* ───────────────────────────── Autenticación ──────────────────────────── */

export const login = (usuario: string, password: string, dispositivoId: string, dispositivoNombre: string) =>
  pedir<RespuestaLogin>('/api/auth/login', {
    metodo: 'POST',
    cuerpo: { usuario, password, dispositivoId, dispositivoNombre },
  });

export const yo = () =>
  pedir<{ usuario: { id: string; nombre: string; rol: string }; restauranteId: string }>(
    '/api/auth/yo',
  );

/* ──────────────────────────── Lectura (pull) ──────────────────────────── */

export const traerMesas = () => pedir<{ mesas: Mesa[] }>('/api/mesas');

export const traerCatalogo = () => pedir<RespuestaCatalogo>('/api/catalogo');

export const traerOrden = (id: string) => pedir<OrdenDetalle>(`/api/ordenes/${id}`);

export const traerOrdenes = (estado?: string) =>
  pedir<{ ordenes: Orden[] }>(`/api/ordenes${estado ? `?estado=${estado}` : ''}`);

/* ─────────────────────────── Escritura (push) ─────────────────────────── */

/**
 * Abrir orden. Mandar el `id` es lo que la vuelve idempotente: si el ACK se
 * perdió y el outbox reintenta, el backend responde 200 con la orden que ya
 * había creado en vez de abrir una segunda.
 */
export const abrirOrden = (datos: {
  id: string;
  /** Se omite cuando viaja `puestoNuevo`: el backend crea el puesto y lo asocia. */
  mesaId?: string | null;
  puestoNuevo?: PuestoNuevo;
  etiqueta?: string | null;
  comensales: number;
  tipo?: TipoOrden;
  notas?: string;
  dispositivoId: string;
  origenOffline: boolean;
}) => pedir<Orden>('/api/ordenes', { metodo: 'POST', cuerpo: datos });

/** Renombrar la cuenta o corregir comensales sobre la marcha. */
export const actualizarOrden = (
  ordenId: string,
  datos: { etiqueta?: string | null; comensales?: number; notas?: string | null },
) => pedir<Orden>(`/api/ordenes/${ordenId}`, { metodo: 'PATCH', cuerpo: datos });

/* ─────────────────────────────── Puestos ──────────────────────────────── */

/**
 * Crear un puesto. Ya no es exclusivo de admin: abrir una mesa es operación
 * del día a día. Si no se manda `numero`, el backend numera solo.
 */
export const crearMesa = (datos: {
  numero?: string;
  nombre?: string;
  zona?: string;
  capacidad?: number;
  esTemporal?: boolean;
}) => pedir<Mesa & { esTemporal: boolean }>('/api/mesas', { metodo: 'POST', cuerpo: datos });

/** Mover una cuenta a otro puesto. Los clientes se cambian de mesa. */
export const moverCuenta = (mesaDestinoId: string, ordenId: string) =>
  pedir<Orden>(`/api/mesas/${mesaDestinoId}/mover`, { metodo: 'POST', cuerpo: { ordenId } });

/** Archivar un puesto. El backend responde 409 si tiene cuentas sin cobrar. */
export const eliminarMesa = (mesaId: string) =>
  pedir<void>(`/api/mesas/${mesaId}`, { metodo: 'DELETE' });

export const agregarItem = (
  ordenId: string,
  datos: { id: string; productoId: string; cantidad: number; notas?: string },
) =>
  pedir<{ item: OrdenItem; orden: Orden }>(`/api/ordenes/${ordenId}/items`, {
    metodo: 'POST',
    cuerpo: datos,
  });

export const actualizarItem = (
  ordenId: string,
  itemId: string,
  datos: { cantidad?: number; notas?: string | null },
) =>
  pedir<{ item: OrdenItem; orden: Orden }>(`/api/ordenes/${ordenId}/items/${itemId}`, {
    metodo: 'PATCH',
    cuerpo: datos,
  });

export const anularItem = (ordenId: string, itemId: string, motivo?: string) =>
  pedir<{ orden: Orden }>(`/api/ordenes/${ordenId}/items/${itemId}`, {
    metodo: 'DELETE',
    cuerpo: { motivo },
  });

export const marcarPropina = (ordenId: string, aceptada: boolean) =>
  pedir<Orden>(`/api/ordenes/${ordenId}/propina`, { metodo: 'POST', cuerpo: { aceptada } });

export const pedirCuenta = (ordenId: string) =>
  pedir<Orden>(`/api/ordenes/${ordenId}/cuenta`, { metodo: 'POST' });

/**
 * Descarta una cuenta abierta por error. Solo funciona si está VACÍA: el
 * backend responde 409 si ya tiene productos o pagos, porque eso ya es una
 * venta que hay que anular con motivo, no un click a deshacer.
 */
export const descartarOrden = (ordenId: string) =>
  pedir<void>(`/api/ordenes/${ordenId}`, { metodo: 'DELETE' });

export const anularOrden = (ordenId: string, motivo: string) =>
  pedir<Orden>(`/api/ordenes/${ordenId}/anular`, { metodo: 'POST', cuerpo: { motivo } });

/* ─────────────────────────────── Turnos ───────────────────────────────── */

/**
 * Abrir caja. Requiere conexión, y está bien que así sea: el turno es un
 * concepto del servidor —lleva consecutivo y arqueo— y el backend rechaza
 * crear órdenes sin uno abierto. Se abre una vez al empezar el día y de ahí
 * en adelante se puede trabajar sin señal.
 */
export const abrirTurno = (baseInicial: number, notas?: string) =>
  pedir<Turno>('/api/turnos/abrir', { metodo: 'POST', cuerpo: { baseInicial, notas } });

export const turnoActual = () => pedir<RespuestaTurnoActual>('/api/turnos/actual');

/** Las mesas que impiden cerrar. La UI las muestra como lista accionable. */
export const ordenesPendientes = () =>
  pedir<{ cantidad: number; pendientes: OrdenPendiente[] }>('/api/turnos/pendientes');

export const cerrarTurno = (turnoId: string, efectivoContado: number, notas?: string) =>
  pedir<{ arqueo: Arqueo; turno: Turno }>(`/api/turnos/${turnoId}/cerrar`, {
    metodo: 'POST',
    cuerpo: { efectivoContado, notas },
  });

/* ──────────────────────────────── Pagos ───────────────────────────────── */

/**
 * Registrar un cobro.
 *
 * Dos detalles del contrato que importan y no son obvios:
 *
 *  • **`monto` se omite a propósito.** Sin él, el backend cobra el saldo
 *    pendiente completo calculado por él mismo. Mandarlo abriría la puerta a
 *    que un total local desactualizado —por una tarifa de propina distinta,
 *    por ejemplo— registrara un pago parcial y dejara la mesa medio cobrada
 *    sin que nadie se entere. El servidor es la autoridad sobre cuánto vale
 *    la cuenta.
 *
 *  • **`recibido` solo viaja en efectivo.** En tarjeta el backend responde
 *    422: no existe "cambio" con tarjeta.
 */
export const registrarPago = (
  ordenId: string,
  datos: {
    id: string;
    metodo: MetodoPago;
    recibido?: number;
    referencia?: string | null;
    ultimosDigitos?: string | null;
  },
) => pedir<RespuestaPago>(`/api/ordenes/${ordenId}/pagos`, { metodo: 'POST', cuerpo: datos });

export const pagosDe = (ordenId: string) =>
  pedir<{ total: string; pagado: string; saldo: string }>(`/api/ordenes/${ordenId}/pagos`);

export const anularPago = (pagoId: string, motivo: string) =>
  pedir<{ orden: Orden; saldoPendiente: string }>(`/api/pagos/${pagoId}`, {
    metodo: 'DELETE',
    cuerpo: { motivo },
  });